import { Link } from 'react-router-dom';
import { useSeo } from '@/hooks/useSeo';
import { categories } from '@/mocks/categories';
import { faqs } from '@/mocks/faqs';
import type { FaqItem } from '@/types/content';
import {
  getFeaturedArticles,
  getHotArticles,
  getLatestArticles,
} from '@/utils/content';
import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import LatestSection from './components/LatestSection';
import RankingSection from './components/RankingSection';
import Testimonials from './components/Testimonials';
import FaqPreview from './components/FaqPreview';
import CtaBand from './components/CtaBand';

const stats = [
  { label: '核对完成的档案', value: '218' },
  { label: '固定栏目', value: '6' },
  { label: '平均每周新增', value: '4' },
  { label: '可追溯来源占比', value: '92%' },
];

export default function Home() {
  const featuredList = getFeaturedArticles(3);
  const featured = featuredList[0];
  const sideList = getLatestArticles(4);
  const latest = getLatestArticles(6);
  const picks = featuredList.slice(1);
  const hot = getHotArticles(5);
  const previewFaqs = (faqs as FaqItem[]).slice(0, 4);

  useSeo({
    title: '美日吃瓜 | 政客丑闻、财阀内幕与官员贪腐深度档案',
    description:
      '美日吃瓜政经频道，深挖政客丑闻与财阀内幕。官员贪腐、选举黑幕、企业造假、学术不端等重磅爆料持续更新，带你直击美日政经圈不为人知的暗面。',
    keywords: '美日吃瓜,美日吃瓜政客丑闻,美日吃瓜财阀,美日吃瓜官员贪腐,美日吃瓜选举黑幕,美日吃瓜企业造假,美日吃瓜学术不端',
    path: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          name: '美日吃瓜',
          alternateName: '政经暗面观察站',
          url: '/',
          inLanguage: 'zh-CN',
          description:
            '专注美国与日本政商圈的深度档案整理，追踪政客丑闻、财阀内幕、官员贪腐、选举黑幕、企业造假与学术不端六大方向。',
          potentialAction: {
            '@type': 'SearchAction',
            target: '/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'Organization',
          name: '美日吃瓜',
          url: '/',
          email: 'editor@meirichigua.com',
          description: '面向中文读者的美日政经深度内容站点。',
        },
        {
          '@type': 'CollectionPage',
          name: '美日吃瓜 首页',
          url: '/',
          inLanguage: 'zh-CN',
        },
      ],
    },
  });

  return (
    <>
      <section className="border-b border-background-300/70 bg-background-100">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-3 md:px-6">
          <span className="flex shrink-0 items-center gap-2 rounded-full bg-primary-600 px-3 py-1 font-label text-xs font-semibold text-background-50">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-megaphone-line" />
            </span>
            爆料速递
          </span>
          <div className="flex flex-1 items-center gap-6 overflow-x-auto">
            {latest.slice(0, 4).map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="whitespace-nowrap font-label text-sm text-foreground-700 transition-colors hover:text-primary-600"
              >
                {article.title}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-10 pt-10 md:px-6 md:pb-14 md:pt-14 animate-fade-up">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:items-end">
          <div className="lg:col-span-2">
            <p className="mb-3 font-label text-xs uppercase tracking-[0.32em] text-primary-600">
              Politics · Business · Under the Table
            </p>
            <h1 className="font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-4xl">
              美日吃瓜：把政经圈的暗面线索，一条条摆到台面上
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              我们只做一件事——围绕一个具体事件，把公开记录里的时间线、资金流向与关键人物关系整理成完整档案。不是速览，不是转述，而是让读者能顺着来源自己核对一遍。
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/category/corruption"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-600 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-compass-3-line" />
                </span>
                从贪腐档案开始读
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-foreground-300 px-5 py-2.5 font-label text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                了解我们的核对标准
              </Link>
            </div>
          </div>

          <dl className="grid grid-cols-2 gap-4 rounded-lg border border-background-300/70 bg-background-100 p-5">
            {stats.map((item) => (
              <div key={item.label}>
                <dt className="font-label text-xs text-foreground-500">{item.label}</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <div className="mx-auto flex max-w-6xl flex-col gap-14 px-4 pb-16 md:gap-20 md:px-6 md:pb-20">
        <div className="animate-fade-up" style={{ animationDelay: '0.12s' }}>
          <HeroSection featured={featured} sideList={sideList} />
        </div>
        <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
          <CategoryGrid categories={categories} />
        </div>
        <LatestSection articles={latest} />
        <RankingSection picks={picks} hot={hot} />
        <Testimonials />
        <FaqPreview items={previewFaqs} />
        <CtaBand />
      </div>
    </>
  );
}